import React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import styles from './NanoProductDetails.module.scss';
import { ReactComponent as Star } from '../../../../../assets/images/icons/star.svg';
import featureImageOne from '../../../../../assets/images/nano/feature_1.png';
import featureImageTwo from '../../../../../assets/images/nano/feature_2.png';
import featureImageThree from '../../../../../assets/images/nano/feature_3.png';
import featureIconOne from '../../../../../assets/images/nano/feature_1_icon.png';
import featureIconTwo from '../../../../../assets/images/nano/feature_2_icon.png';
import featureIconThree from '../../../../../assets/images/nano/feature_3_icon.png';
import socialProofZero from '../../../../../assets/images/nano/social_proof_0.png';
import socialProofOne from '../../../../../assets/images/nano/social_proof_1.png';
import socialProofTwo from '../../../../../assets/images/nano/social_proof_2.png';
import socialProofThree from '../../../../../assets/images/nano/social_proof_3.png';
import socialProofFour from '../../../../../assets/images/nano/social_proof_4.png';
import socialProofFive from '../../../../../assets/images/nano/social_proof_5.png';
import socialProofSix from '../../../../../assets/images/nano/social_proof_6.png';
import socialProofSeven from '../../../../../assets/images/nano/social_proof_7.png';
import Flickity from 'react-flickity-component';
import collarTestimonialImage from '../../../../../assets/images/nano/collar_testimonial.jpeg';
import miniTestimonialImage from '../../../../../assets/images/nano/mini_testimonial.jpeg';

const getTestimonialTitle = (productType: 'collar' | 'mini', t: TFunction) => {
  if (productType === 'collar') {
    return t('the_smartest_collar_ever_built');
  } else {
    return t('the_worlds_smallest_gps_tracker');
  }
};

const getTestimonialBackgroundImage = (productType: 'collar' | 'mini') => {
  let imageUrl;
  if (productType === 'collar') {
    imageUrl = collarTestimonialImage;
  }
  if (productType === 'mini') {
    imageUrl = miniTestimonialImage;
  }
  return `linear-gradient(0deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.16) 100%), url(${imageUrl})`;
};

export default function NanoProductDetails({ productType }: { productType: 'collar' | 'mini' }) {
  const { t } = useTranslation();
  const testimonialTitle = getTestimonialTitle(productType, t);
  const testimonialBackgroundImage = getTestimonialBackgroundImage(productType);

  return (
    <div className={styles.nanoProductDetailsContainer}>
      <div className={styles.testimonialContainer}>
        <div className={styles.testimonialTitle}>{testimonialTitle}</div>
        <div className={styles.testimonialImageContainer} style={{ backgroundImage: testimonialBackgroundImage }}>
          <div className={styles.testimonialStars}>
            <Star />
            <Star />
            <Star />
            <Star />
            <Star />
          </div>
          <div className={styles.testimonialMessage}>
            {t('within_minutes_of_our_dog_wandering_off_i_was_able_to_locate_him_in_the_app_and_find_him')}
          </div>
          <div className={styles.testimonialUser}>{t('buddys_dad')}</div>
        </div>
      </div>
      <div className={styles.featuresContainer}>
        <div className={styles.featureContainer}>
          <div className={styles.featureDescription}>
            <img className={styles.featureIcon} src={featureIconOne} alt={t('live_tracking_icon')} />
            {t('get_them_home_as_soon_as_possible_with_escape_alerts_live_tracking')}
          </div>
          <img className={styles.featureImage} src={featureImageOne} alt={t('live_tracking_feature')} />
        </div>
        <div className={styles.featureContainer}>
          <div className={styles.featureDescription}>
            <img className={styles.featureIcon} src={featureIconTwo} alt={t('activity_tracking_icon')} />
            {t('stay_motivated_with_steps_and_activity_goals_personalized_to_your_dog')}
          </div>
          <img className={styles.featureImage} src={featureImageTwo} alt={t('activity_tracking_feature')} />
        </div>
        <div className={styles.featureContainer}>
          <div className={styles.featureDescription}>
            <img className={styles.featureIcon} src={featureIconThree} alt={t('health_report_icon')} />
            {t('share_a_detailed_health_report_with_your_vet_to_spot_trends_and_potential_issues')}
          </div>
          <img className={styles.featureImage} src={featureImageThree} alt={t('health_report_feature')} />
        </div>
      </div>
      <div className={styles.socialProofContainer}>
        <div className={styles.socialProofTitle}>{t('connect_with_dogs_dog_lovers_across_the_country')}</div>
        <div className={styles.socialProofImageContainer}>
          <img className={styles.socialProofImage} src={socialProofZero} alt={t('social_pet_zero')} />
          <img className={styles.socialProofImage} src={socialProofOne} alt={t('social_pet_one')} />
          <img className={styles.socialProofImage} src={socialProofTwo} alt={t('social_pet_two')} />
          <img className={styles.socialProofImage} src={socialProofThree} alt={t('social_pet_three')} />
          <img className={styles.socialProofImage} src={socialProofFour} alt={t('social_pet_four')} />
          <img className={styles.socialProofImage} src={socialProofFive} alt={t('social_pet_five')} />
          <img className={styles.socialProofImage} src={socialProofSix} alt={t('social_pet_six')} />
          <img className={styles.socialProofImage} src={socialProofSeven} alt={t('social_pet_seven')} />
        </div>
        <div className={styles.socialProofImageCarousel}>
          <Flickity options={{ autoPlay: 3000, wrapAround: true, prevNextButtons: false }}>
            <div className={styles.carouselCell}>
              <img className={styles.socialProofImage} src={socialProofZero} alt={t('social_pet_zero')} />
            </div>
            <div className={styles.carouselCell}>
              <img className={styles.socialProofImage} src={socialProofOne} alt={t('social_pet_one')} />
            </div>
            <div className={styles.carouselCell}>
              <img className={styles.socialProofImage} src={socialProofTwo} alt={t('social_pet_two')} />
            </div>
            <div className={styles.carouselCell}>
              <img className={styles.socialProofImage} src={socialProofThree} alt={t('social_pet_three')} />
            </div>
            <div className={styles.carouselCell}>
              <img className={styles.socialProofImage} src={socialProofFour} alt={t('social_pet_four')} />
            </div>
            <div className={styles.carouselCell}>
              <img className={styles.socialProofImage} src={socialProofFive} alt={t('social_pet_five')} />
            </div>
            <div className={styles.carouselCell}>
              <img className={styles.socialProofImage} src={socialProofSix} alt={t('social_pet_six')} />
            </div>
            <div className={styles.carouselCell}>
              <img className={styles.socialProofImage} src={socialProofSeven} alt={t('social_pet_seven')} />
            </div>
          </Flickity>
        </div>
      </div>
    </div>
  );
}
